import {
  dcloudFetch,
  DcloudLinkRemedy,
  DcloudRequestMethod,
  DcloudTopologyTokenHint,
  DcloudUserTokenStatus,
} from './dcloudService';

/**
 * dCloud Topology Builder (TBv3).
 *
 * Everything here goes through the Toolbox backend at /api/dcloud/topology/,
 * which attaches the caller's dCloud ui-token. The browser never holds a dCloud
 * token, so TBv3 cannot be called directly and the HAL hrefs in responses have
 * already been rewritten onto that prefix by the proxy.
 *
 * Topology Builder is per-person: unlike the session API there is no service
 * account fallback, so an unlinked caller gets a 409 `dcloud_link_required`
 * rather than someone else's topologies.
 */

export const TOPOLOGY_PROXY_PREFIX = 'dcloud/topology';

// -----------------------------------------------------------------------------
// HAL
// -----------------------------------------------------------------------------

export type HalLink = {
  href: string;
  templated?: boolean;
  type?: string;
  [key: string]: any;
};

export type HalLinks = Record<string, HalLink | HalLink[]>;

export type HalResource = {
  uid?: string;
  _links?: HalLinks;
  _embedded?: Record<string, any>;
};

/**
 * The rows out of a HAL collection.
 *
 * `_embedded` is keyed by relation name and TBv3 is not always consistent about
 * which one it uses, so preferred names are tried first and then any array in
 * `_embedded`. An empty collection legitimately omits `_embedded` entirely.
 */
export function embeddedRows<T = any>(payload: any, ...preferredRels: string[]): T[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as T[];

  const embedded = payload._embedded;

  if (!embedded || typeof embedded !== 'object') return [];

  for (const rel of preferredRels) {
    if (Array.isArray(embedded[rel])) return embedded[rel] as T[];
  }

  const firstArray = Object.values(embedded).find((value) => Array.isArray(value));

  return (firstArray as T[]) || [];
}

export const halHref = (
  resource: HalResource | null | undefined,
  rel: string,
): string | null => {
  const link = resource?._links?.[rel];
  const first = Array.isArray(link) ? link[0] : link;

  return first?.href || null;
};

// -----------------------------------------------------------------------------
// Resources
// -----------------------------------------------------------------------------

export type TopologyStatus = 'DRAFT' | 'CUSTOM_CONTENT' | 'SAVED_CONTENT' | string;
export type TopologyDatacenter = 'RTP' | 'SJC' | string;
export type TopologyAccessRole = 'OWNER' | 'EDITOR' | string;

export type TopologyAccessRoleAssignment = HalResource & {
  uid?: string;
  email?: string;
  accessRole?: TopologyAccessRole;
  grantedBy?: string;
  grantedOn?: string;
};

export type Topology = HalResource & {
  uid: string;
  name?: string;
  description?: string;
  datacenter?: TopologyDatacenter;
  notes?: string;
  lastUpdated?: string;
  status?: TopologyStatus;
  /** Set once the topology has been pushed to dCloud as a demo. */
  demoId?: number | string | null;
  demoPromoted?: boolean;
  demoPublished?: boolean;
  /** True when the caller is an EDITOR rather than the owner. */
  sharedWithCurrentUser?: boolean;
  /** False when there are saved changes that have not been pushed to dCloud. */
  inSyncWithDcloud?: boolean;
  guestAccessEnabled?: boolean;
  activeTemplate?: boolean;
  launchPanelEnabled?: boolean;
  latestVersion?: { uid?: string } & Record<string, any>;
  syncedVersion?: { uid?: string } & Record<string, any>;
  accessRoleAssignments?: TopologyAccessRoleAssignment[];
  warnings?: any[];
};

/** Consumption against the per-topology ceiling, as TBv3 reports it. */
export type TopologyLimitUsage = { current?: number; limit?: number };

export type TopologyLimits = HalResource & {
  cpuCores?: TopologyLimitUsage;
  memoryMb?: TopologyLimitUsage;
  natRules?: TopologyLimitUsage;
  hardware?: TopologyLimitUsage;
};

/** Account-wide ceilings and warning thresholds, identical for every topology. */
export type TopologyValidationLimits = {
  maxRdpSshEnabledVms?: number;
  maxInboundProxyRules?: number;
  maxMemoryMb?: number;
  warningMemoryMb?: number;
  maxCpuCores?: number;
  warningCpuCores?: number;
  routedNetworkConnections?: number;
  unroutedNetworkConnections?: number;
  invalidInboundProxyMacAddresses?: string[];
  invalidInboundProxyIpAddresses?: string[];
  [key: string]: any;
};

export type TopologyInventoryNetwork = HalResource & {
  id?: string;
  name?: string;
  description?: string;
  routed?: boolean;
  [key: string]: any;
};

export type TopologyNetwork = HalResource & {
  uid?: string;
  name?: string;
  description?: string;
  inventoryNetwork?: TopologyInventoryNetwork;
  [key: string]: any;
};

export type TopologyVmNetworkInterface = {
  assignDhcp?: boolean;
  macAddress?: string;
  name?: string;
  network?: { uid?: string } & Record<string, any>;
  ipAddress?: string;
  [key: string]: any;
};

export type TopologyVm = HalResource & {
  uid?: string;
  name?: string;
  description?: string;
  cpuCores?: number;
  memoryMb?: number;
  vmNetworkInterfaces?: TopologyVmNetworkInterface[];
  remoteAccess?: any;
  [key: string]: any;
};

export type TopologyDiagramAssetType =
  | 'VM'
  | 'NETWORK'
  | 'ANY_CONNECT'
  | 'SAAS'
  | 'INTERNET_CLOUD'
  | 'VPOD_GATEWAY';

export type TopologyDrawingType = 'BOX' | 'TEXT' | 'ICON';

/**
 * Node placement. Assets carry x1/y1 (plus x2 for a stretched network bar);
 * free-form drawings carry drawingType and their own geometry.
 */
export type TopologyNodeDisplay = {
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  icon?: string;
  hidden?: boolean;
  drawingType?: TopologyDrawingType;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  text?: string;
  name?: string;
  data?: any[];
  [key: string]: any;
};

export type TopologyDiagramNode = HalResource & {
  uid?: string;
  /** Absent on pure drawings (box, text, standalone icon). */
  assetType?: TopologyDiagramAssetType;
  name?: string;
  display?: TopologyNodeDisplay;
  [key: string]: any;
};

export type TopologyDiagramConnection = HalResource & {
  uid?: string;
  type?: 'PHYSICAL' | string;
  nodes?: { from?: { uid?: string }; to?: { uid?: string } };
};

export type TopologyDiagram = HalResource & {
  topology?: Partial<Topology>;
  topologyVersion?: Record<string, any>;
  nodes?: TopologyDiagramNode[];
  connections?: TopologyDiagramConnection[];
};

export type TopologyUserRole =
  | 'ROLE_USER'
  | 'ROLE_INTERNAL_USER'
  | 'ROLE_PUBLIC_CLOUD_USER'
  | string;

export type TopologyCurrentUser = HalResource & {
  email?: string;
  userRoles?: { role?: TopologyUserRole }[];
  [key: string]: any;
};

// -----------------------------------------------------------------------------
// Errors and capabilities
// -----------------------------------------------------------------------------

export type TopologyErrorCode =
  | 'dcloud_link_required'
  | 'dcloud_ui_token_required'
  | 'dcloud_topology_token_rejected'
  | 'dcloud_topology_disabled'
  | 'dcloud_topology_route_not_allowed'
  | 'dcloud_topology_method_not_allowed'
  | 'dcloud_topology_unreachable'
  | 'dcloud_topology_stale_resource'
  | string;

/**
 * The remediation block the proxy attaches to any 401/409/503.
 *
 * `remedy` is the field to branch on. The codes alone are not enough:
 * `dcloud_topology_token_rejected` covers both an expired token TBv3 would have
 * accepted and a live token from the wrong issuer, and only the backend knows
 * which - see topology_service._token_rejected_detail.
 */
export type TopologyRemediation = {
  remedy?: DcloudLinkRemedy;
  /** Starts a portal SSO round trip. Useless when remedy is `paste_token`. */
  login_url?: string;
  /** Where a token captured from the browser is submitted. */
  adopt_url?: string;
  /** Present only when a full sign-in against TBv3's own provider can complete. */
  topology_login_url?: string;
  /** localStorage key names and the issuer TBv3 trusts. */
  token_hint?: DcloudTopologyTokenHint;
};

export type TopologyError = Error & {
  status?: number;
  payload?: TopologyRemediation & {
    error?: TopologyErrorCode;
    detail?: string;
  };
};

export const topologyErrorCode = (error: unknown): TopologyErrorCode | null =>
  (error as TopologyError)?.payload?.error || null;

/**
 * What the backend says will fix this failure, or null when it did not say.
 *
 * Falls back to inferring from the code so an older backend - one deployed before
 * `remedy` existed - still drives the right UI instead of none.
 */
export const topologyErrorRemedy = (error: unknown): DcloudLinkRemedy | null => {
  const payload = (error as TopologyError)?.payload;

  if (payload?.remedy) return payload.remedy;

  switch (payload?.error) {
    case 'dcloud_link_required':
      return 'sign_in';
    case 'dcloud_ui_token_required':
    case 'dcloud_topology_token_rejected':
      return 'paste_token';
    case 'dcloud_topology_unreachable':
      return 'wait';
    default:
      return null;
  }
};

export const topologyErrorRemediation = (error: unknown): TopologyRemediation => {
  const payload = (error as TopologyError)?.payload || {};

  return {
    remedy: topologyErrorRemedy(error) || undefined,
    login_url: payload.login_url,
    adopt_url: payload.adopt_url,
    topology_login_url: payload.topology_login_url,
    token_hint: payload.token_hint,
  };
};

/**
 * True when the resource was edited elsewhere since it was loaded.
 *
 * Topology Builder guards updates with ETags, so an edit made in its own UI (or
 * another tab) invalidates the copy this one was built from. Refetching and
 * reapplying is the fix; retrying the same write is not.
 */
export const isTopologyStaleError = (error: unknown): boolean =>
  topologyErrorCode(error) === 'dcloud_topology_stale_resource';

/**
 * True when the fix is an identity change rather than a retry: no link at all, a
 * link holding an IdP token instead of a dCloud one, or a token TBv3 refused.
 *
 * Deliberately does not say *which* change - use topologyErrorRemedy for that.
 */
export const isTopologyLinkError = (error: unknown): boolean => {
  const code = topologyErrorCode(error);

  return (
    code === 'dcloud_link_required' ||
    code === 'dcloud_ui_token_required' ||
    code === 'dcloud_topology_token_rejected'
  );
};

/**
 * True when only a token pasted from the caller's own Topology Builder tab will do.
 *
 * The distinguishing case: the caller is signed in, the link is live, and TBv3 still
 * answers 401 because the token came from the portal's Duo tenant rather than
 * id.cisco.com. Re-running sign-in cannot fix it, so the UI must not offer that.
 */
export const isTopologyPasteRequired = (error: unknown): boolean =>
  topologyErrorRemedy(error) === 'paste_token';

export type TopologyCapabilities = TopologyRemediation & {
  enabled: boolean;
  /** A live per-user token is held. Says nothing about whether TBv3 accepts it. */
  usable: boolean;
  /** TBv3 honors the issuer of the stored token. */
  token_accepted?: boolean;
  /**
   * Both of the above. Gate real requests on this: `usable` alone is true for a
   * portal SSO link, which then 401s on every call.
   */
  ready?: boolean;
  api_base?: string;
  proxy_prefix?: string;
  dcloud_user_token?: DcloudUserTokenStatus;
  collections?: string[];
  overview_sections?: string[];
  inventory_sections?: string[];
};

/**
 * Whether Topology Builder calls can be expected to succeed.
 *
 * Prefers the backend's own `ready`, falling back to `usable` when talking to a
 * deployment that predates it - there, discovering the problem via a 401 is still
 * the only option.
 */
export const topologyCapabilitiesReady = (
  capabilities?: TopologyCapabilities | null,
): boolean => {
  if (!capabilities?.enabled) return false;

  return capabilities.ready ?? Boolean(capabilities.usable);
};

/** A section of the overview that failed while the rest succeeded. */
export type TopologySectionError = {
  error: TopologyErrorCode;
  detail?: string;
  status?: number;
};

export const topologySectionError = (section: any): TopologySectionError | null =>
  section && !Array.isArray(section) && typeof section === 'object' && section.error
    ? (section as TopologySectionError)
    : null;

export type TopologyOverview = {
  topology: Topology;
  limits?: TopologyLimits | TopologySectionError;
  networks?: any;
  vms?: any;
  hardwareItems?: any;
  remoteAccess?: any;
  natRules?: any;
  inboundProxyRules?: any;
  externalDnsRecords?: any;
  mailServers?: any;
  telephonyItems?: any;
  saasIntegrations?: any;
  accessRoleAssignments?: any;
  diagram?: TopologyDiagram | TopologySectionError;
  inventoryNetworks?: any;
  inventoryTemplateVms?: any;
  inventoryDnsAssets?: any;
  inventoryTelephonyItems?: any;
};

// -----------------------------------------------------------------------------
// Request layer
// -----------------------------------------------------------------------------

/**
 * TBv3 is not per-data-center - one service serves RTP and SJC, and a topology
 * names its own datacenter - so these calls skip the preferred_data_center
 * parameter the session endpoints need.
 */
async function topologyRequest<T = any>(
  method: DcloudRequestMethod,
  path: string,
  options: { params?: Record<string, any>; body?: any; signal?: AbortSignal } = {},
): Promise<T> {
  const { data } = await dcloudFetch<T>(method, path, {
    ...options,
    skipDataCenter: true,
  });

  return data;
}

const topologyPath = (upstreamPath: string): string =>
  `${TOPOLOGY_PROXY_PREFIX}/${String(upstreamPath).replace(/^\/+/, '')}`;

/**
 * Follow a HAL href from a previous response.
 *
 * The proxy rewrote them to /api/dcloud/topology/..., so anything else is either
 * a browser-destined link (a dCloud schedule page) or an un-rewritten upstream
 * URL the browser has no token for; both are refused rather than fetched.
 */
export const followTopologyLink = async <T = any>(href: string): Promise<T> => {
  const path = String(href || '').replace(/^https?:\/\/[^/]+/, '');

  if (!path.startsWith(`/api/${TOPOLOGY_PROXY_PREFIX}/`)) {
    throw new Error(`Not a Topology Builder link: ${href}`);
  }

  return topologyRequest<T>('GET', path);
};

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

export const fetchTopologyCapabilities = (): Promise<TopologyCapabilities> =>
  topologyRequest<TopologyCapabilities>('GET', `${TOPOLOGY_PROXY_PREFIX}/capabilities`);

export const fetchTopologyCurrentUser = (): Promise<TopologyCurrentUser> =>
  topologyRequest<TopologyCurrentUser>('GET', topologyPath('current-user'));

export const fetchTopologyValidationLimits = (): Promise<TopologyValidationLimits> =>
  topologyRequest<TopologyValidationLimits>('GET', topologyPath('validation-limits'));

export const fetchTopologies = async (
  params: Record<string, any> = {},
): Promise<{ topologies: Topology[]; raw: any }> => {
  const raw = await topologyRequest<any>('GET', topologyPath('topologies'), { params });

  return { topologies: embeddedRows<Topology>(raw, 'topologies'), raw };
};

export const fetchTopology = (uid: string): Promise<Topology> =>
  topologyRequest<Topology>('GET', topologyPath(`topologies/${uid}`));

export const fetchTopologyLimits = (uid: string): Promise<TopologyLimits> =>
  topologyRequest<TopologyLimits>('GET', topologyPath(`topologies/${uid}/limits`));

export const fetchTopologyDiagram = (uid: string): Promise<TopologyDiagram> =>
  topologyRequest<TopologyDiagram>('GET', topologyPath(`topologies/${uid}/diagram`));

/**
 * Any sub-resource of a topology by name, e.g. 'networks', 'vms',
 * 'inventory-template-vms', 'saas-integrations'. The backend allowlist decides
 * what is reachable; unknown names come back as 404 dcloud_topology_route_not_allowed.
 */
export const fetchTopologyCollection = async <T = any>(
  uid: string,
  collection: string,
  params: Record<string, any> = {},
): Promise<{ rows: T[]; raw: any }> => {
  const raw = await topologyRequest<any>(
    'GET',
    topologyPath(`topologies/${uid}/${collection}`),
    { params },
  );

  return { rows: embeddedRows<T>(raw), raw };
};

/**
 * The topology plus every sub-resource its configuration tabs need, composed
 * server-side. `inventory` adds the add-dialog catalogs, which are large.
 */
export const fetchTopologyOverview = (
  uid: string,
  options: { sections?: string[]; inventory?: boolean } = {},
): Promise<TopologyOverview> =>
  topologyRequest<TopologyOverview>(
    'GET',
    `${TOPOLOGY_PROXY_PREFIX}/topologies/${uid}/overview`,
    {
      params: {
        ...(options.sections?.length ? { sections: options.sections.join(',') } : {}),
        ...(options.inventory ? { inventory: 1 } : {}),
      },
    },
  );

export const fetchTopologyDocumentation = (uid: string): Promise<any> =>
  topologyRequest<any>('GET', topologyPath(`documentation/${uid}`));

// -----------------------------------------------------------------------------
// Data-center-scoped catalogs
//
// These are the only reads here that are per data center, and they are why: a demo id is not
// global, and neither is a template. The same lab has a different demo id in RTP and in
// London, and a template published in one is not necessarily published in the other. So
// anything that needs to know "which lab, where" reads it from the data center rather than
// storing a copy that goes stale when dCloud republishes.
// -----------------------------------------------------------------------------

export type TopologyDatacenterCatalog =
  | 'inventory-demos'
  | 'inventory-template-topologies'
  | 'templated-topologies'
  | 'templated-inventory-vms';

/**
 * A demo the data center publishes.
 *
 * Loosely typed on purpose: TBv3 names the identifier `uid` on some collections and `id` on
 * others, and reading the wrong one silently yields a configuration that matches no session.
 * `dcloudDemoUid` below picks whichever is present rather than guessing here.
 */
export type TopologyDatacenterDemo = HalResource & {
  uid?: string | number;
  id?: string | number;
  demoUid?: string | number;
  name?: string;
  title?: string;
  description?: string;
  datacenter?: string;
  [key: string]: any;
};

/**
 * A template topology, at the capacity it was published at.
 *
 * The capacity - dCloud's "t-shirt size" - is what decides what a pod built from this template
 * can hold. It arrives under any of several names depending on the collection, so read it with
 * `dcloudTemplateSize`.
 */
export type TopologyTemplateTopology = HalResource & {
  uid?: string;
  name?: string;
  description?: string;
  size?: string;
  sizeName?: string;
  templateSize?: string;
  datacenter?: string;
  [key: string]: any;
};

/**
 * One data center's catalog.
 *
 * The data center code is lowercased here because the backend allowlist only matches lowercase
 * (`datacenters/[a-z0-9-]+/...`); an upper-case one comes back 404 route-not-allowed, which
 * reads like the catalog does not exist rather than like a typo.
 */
export const fetchDatacenterCatalog = async <T = any>(
  dataCenter: string,
  catalog: TopologyDatacenterCatalog,
  params: Record<string, any> = {},
): Promise<{ rows: T[]; raw: any }> => {
  const dc = String(dataCenter || '')
    .trim()
    .toLowerCase();

  if (!dc) {
    throw new Error('A data center is needed to read its catalog.');
  }

  const raw = await topologyRequest<any>(
    'GET',
    topologyPath(`datacenters/${encodeURIComponent(dc)}/${catalog}`),
    { params },
  );

  return { rows: embeddedRows<T>(raw), raw };
};

export const fetchDatacenterDemos = (
  dataCenter: string,
  params: Record<string, any> = {},
): Promise<{ rows: TopologyDatacenterDemo[]; raw: any }> =>
  fetchDatacenterCatalog<TopologyDatacenterDemo>(dataCenter, 'inventory-demos', params);

export const fetchDatacenterTemplateTopologies = (
  dataCenter: string,
  params: Record<string, any> = {},
): Promise<{ rows: TopologyTemplateTopology[]; raw: any }> =>
  fetchDatacenterCatalog<TopologyTemplateTopology>(
    dataCenter,
    'inventory-template-topologies',
    params,
  );

/** The demo's numeric id, whichever field it arrived in. Null when there is not one. */
export const dcloudDemoUid = (demo?: TopologyDatacenterDemo | null): number | null => {
  const raw = demo?.demoUid ?? demo?.uid ?? demo?.id;
  const parsed = Number(raw);

  return raw !== undefined && raw !== null && Number.isFinite(parsed) ? parsed : null;
};

export const dcloudDemoName = (demo?: TopologyDatacenterDemo | null): string =>
  String(demo?.name || demo?.title || '');

/** The template's capacity label, whichever field it arrived in. */
export const dcloudTemplateSize = (template?: TopologyTemplateTopology | null): string =>
  String(template?.size || template?.sizeName || template?.templateSize || '');

// -----------------------------------------------------------------------------
// Writes
//
// TBv3 addresses every resource on its own and names the owning topology in the
// body, so creates go to the top-level collection rather than under /topologies.
// -----------------------------------------------------------------------------

export type CreateTopologyRequest = {
  name: string;
  description?: string;
  datacenter?: TopologyDatacenter;
  notes?: string;
  [key: string]: any;
};

export const createTopology = (payload: CreateTopologyRequest): Promise<Topology> =>
  topologyRequest<Topology>('POST', topologyPath('topologies'), { body: payload });

export const updateTopology = (
  uid: string,
  patch: Partial<Topology> & Record<string, any>,
): Promise<Topology> =>
  topologyRequest<Topology>('PATCH', topologyPath(`topologies/${uid}`), { body: patch });

export const deleteTopology = (uid: string): Promise<void> =>
  topologyRequest<void>('DELETE', topologyPath(`topologies/${uid}`));

/**
 * Copy a topology. The copy always starts as DRAFT with its own uid, whether the
 * source was a draft, a saved topology, or a template.
 */
export const cloneTopology = (
  uid: string,
  payload: { name: string; description?: string },
): Promise<Topology> =>
  topologyRequest<Topology>('POST', topologyPath(`topologies/${uid}/clones`), {
    body: payload,
  });

export type CreateNetworkRequest = {
  topologyUid: string;
  name: string;
  description?: string;
  /** From inventory-networks, e.g. 'L3-VLAN-3'. */
  inventoryNetworkId: string;
};

export const createTopologyNetwork = ({
  topologyUid,
  name,
  description,
  inventoryNetworkId,
}: CreateNetworkRequest): Promise<TopologyNetwork> =>
  topologyRequest<TopologyNetwork>('POST', topologyPath('networks'), {
    body: {
      name,
      description,
      inventoryNetwork: { id: inventoryNetworkId },
      topology: { uid: topologyUid },
    },
  });

export const deleteTopologyNetwork = (uid: string): Promise<void> =>
  topologyRequest<void>('DELETE', topologyPath(`networks/${uid}`));

export type CloneVmRequest = {
  topologyUid: string;
  /** uid of the inventory-template-vms entry being cloned in. */
  vmUid: string;
  vmNetworkInterfaces?: TopologyVmNetworkInterface[];
};

export const cloneTopologyVm = ({
  topologyUid,
  vmUid,
  vmNetworkInterfaces = [],
}: CloneVmRequest): Promise<TopologyVm> =>
  topologyRequest<TopologyVm>('POST', topologyPath('vm-clones'), {
    body: {
      topology: { uid: topologyUid },
      vmUid,
      vmNetworkInterfaces,
    },
  });

export const deleteTopologyVm = (uid: string): Promise<void> =>
  topologyRequest<void>('DELETE', topologyPath(`vms/${uid}`));

export const updateTopologyVm = (
  uid: string,
  patch: Partial<TopologyVm> & Record<string, any>,
): Promise<TopologyVm> =>
  topologyRequest<TopologyVm>('PATCH', topologyPath(`vms/${uid}`), { body: patch });

/**
 * Move a node on the diagram. Only the display block is sent, which is what the
 * builder does on drag-release.
 */
export const updateDiagramNode = (
  uid: string,
  display: TopologyNodeDisplay,
): Promise<TopologyDiagramNode> =>
  topologyRequest<TopologyDiagramNode>('PATCH', topologyPath(`nodes/${uid}`), {
    body: { display },
  });

export const deleteDiagramNode = (uid: string): Promise<void> =>
  topologyRequest<void>('DELETE', topologyPath(`nodes/${uid}`));

const createDiagramNode = (
  topologyUid: string,
  display: TopologyNodeDisplay,
): Promise<TopologyDiagramNode> =>
  topologyRequest<TopologyDiagramNode>('POST', topologyPath('nodes'), {
    body: { display, topology: { uid: topologyUid } },
  });

export const createDiagramText = (
  topologyUid: string,
  { text = 'Text', x = 0, y = 0 }: { text?: string; x?: number; y?: number },
): Promise<TopologyDiagramNode> =>
  createDiagramNode(topologyUid, { drawingType: 'TEXT', text, x, y });

export const createDiagramBox = (
  topologyUid: string,
  {
    x = 0,
    y = 0,
    width = 100,
    height = 100,
  }: { x?: number; y?: number; width?: number; height?: number },
): Promise<TopologyDiagramNode> =>
  createDiagramNode(topologyUid, { drawingType: 'BOX', width, height, x, y });

/** Standalone icons use x1/y1 rather than x/y, matching the builder's payload. */
export const createDiagramIcon = (
  topologyUid: string,
  {
    icon,
    name,
    x1 = 0,
    y1 = 0,
  }: { icon: string; name?: string; x1?: number; y1?: number },
): Promise<TopologyDiagramNode> =>
  createDiagramNode(topologyUid, {
    drawingType: 'ICON',
    data: [],
    icon,
    name,
    x1,
    y1,
  });

export const createDiagramConnection = (
  topologyUid: string,
  fromUid: string,
  toUid: string,
): Promise<TopologyDiagramConnection> =>
  topologyRequest<TopologyDiagramConnection>('POST', topologyPath('connections'), {
    body: {
      type: 'PHYSICAL',
      nodes: { from: { uid: fromUid }, to: { uid: toUid } },
      topology: { uid: topologyUid },
    },
  });

export const deleteDiagramConnection = (uid: string): Promise<void> =>
  topologyRequest<void>('DELETE', topologyPath(`connections/${uid}`));

/**
 * Push to dCloud: republishes the saved topology onto its demo, which is what
 * clears `inSyncWithDcloud: false`. Only a topology that already has a demoId
 * can be pushed.
 */
export const pushTopologyToDcloud = ({
  demoId,
  topologyUid,
}: {
  demoId: number | string;
  topologyUid: string;
}): Promise<any> =>
  topologyRequest<any>('PUT', topologyPath(`demos/${demoId}`), {
    body: { id: String(demoId), topology: { uid: topologyUid } },
  });

export const grantTopologyAccess = (payload: {
  topologyUid: string;
  email: string;
  accessRole?: TopologyAccessRole;
}): Promise<TopologyAccessRoleAssignment> =>
  topologyRequest<TopologyAccessRoleAssignment>(
    'POST',
    topologyPath('access-role-assignments'),
    {
      body: {
        email: payload.email,
        accessRole: payload.accessRole || 'EDITOR',
        topology: { uid: payload.topologyUid },
      },
    },
  );

export const revokeTopologyAccess = (uid: string): Promise<void> =>
  topologyRequest<void>('DELETE', topologyPath(`access-role-assignments/${uid}`));

// -----------------------------------------------------------------------------
// Display helpers
// -----------------------------------------------------------------------------

export const TOPOLOGY_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  CUSTOM_CONTENT: 'Custom content',
  SAVED_CONTENT: 'Saved',
};

export const topologyStatusLabel = (status?: string): string =>
  (status && TOPOLOGY_STATUS_LABELS[status]) || status || 'Unknown';

export const topologyAccessRole = (
  topology: Topology | null | undefined,
  email?: string | null,
): TopologyAccessRole | null => {
  if (!topology) return null;

  const match = (topology.accessRoleAssignments || []).find(
    (assignment) =>
      (assignment.email || '').toLowerCase() === (email || '').toLowerCase(),
  );

  if (match?.accessRole) return match.accessRole;

  return topology.sharedWithCurrentUser ? 'EDITOR' : 'OWNER';
};

export const formatTopologyMemory = (memoryMb?: number | null): string => {
  if (memoryMb === null || memoryMb === undefined) return '-';

  return memoryMb >= 1024
    ? `${Math.round((memoryMb / 1024) * 10) / 10} GB`
    : `${memoryMb} MB`;
};

/**
 * How close a usage figure is to its ceiling. `warn` mirrors Topology Builder's
 * own soft thresholds (32 cores, 96 GB), which are advisory, not enforced.
 */
export const topologyLimitSeverity = (
  usage?: TopologyLimitUsage | null,
  warnAt?: number | null,
): 'ok' | 'warn' | 'over' => {
  const current = usage?.current ?? 0;
  const limit = usage?.limit ?? 0;

  if (limit && current > limit) return 'over';
  if (warnAt && current >= warnAt) return 'warn';
  if (limit && current >= limit * 0.9) return 'warn';

  return 'ok';
};

export const formatTopologyDate = (value?: string | null): string => {
  if (!value) return '-';

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};
